import useSWR, { SWRResponse } from 'swr';
import { swrFetcher } from 'packages/lib/utils/fetch';
import { isTask, type Task } from 'packages/lib/api/annotations/contract/task';
import { patch } from 'packages/lib/utils/legacy-tsapi/rest-helpers';
import { isArrayOf } from 'packages/lib/common-types/types';
import { UnexpectedResponseType } from 'packages/lib/utils/error';
import {
  AnnotationState,
  TaskState,
  taskStateZod,
} from 'packages/lib/api/annotations/contract/enums';

export const useTasksByTaskGroup = (
  taskGroupToken?: string
): SWRResponse<Task[], UnexpectedResponseType> =>
  useSWR<Task[]>(
    taskGroupToken
      ? `/tasks/data?taskGroupToken=${taskGroupToken}&orderBy=-createdAt`
      : null,
    (url: string) =>
      swrFetcher<Task[]>(url, {
        baseURL: 'annotations',
        typeGuard: isArrayOf(isTask),
      }),
    { revalidateOnFocus: false, suspense: false }
  );

export const assignTaskToUser = (
  taskToken: string,
  email: string | undefined
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    assignee: email,
    state: 'InProgress',
  });
};

const unassignAnnotationStateTarget: Partial<
  Record<AnnotationState, AnnotationState>
> = {
  QaToDo: 'QaToDo',
  QaInProgress: 'QaToDo',
  QaDone: 'QaToDo',
  AuditToDo: 'AuditToDo',
  AuditInProgress: 'AuditToDo',
  AuditDone: 'AuditToDo',
};

export const unassignTask = (
  taskToken: string,
  annotationState: AnnotationState
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    assignee: null,
    annotationState: unassignAnnotationStateTarget[annotationState] ?? 'ToDo',
  });
};

export const resetTask = (
  taskToken: string,
  clearComments = false
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    assignee: null,
    state: 'ToDo',
    ...(clearComments && { comments: '' }),
  });
};

export const changeTaskStateAPI = (
  taskToken: string,
  newState: TaskState
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    state: newState,
  });
};

export const changeTaskAnnotationStateAPI = (
  taskToken: string,
  newState: AnnotationState
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    annotationState: newState,
  });
};

export const rejectTaskAPI = (
  taskToken: string,
  reason: string
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    state: taskStateZod.enum.Rejected,
    comments: reason,
  });
};
